import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import { PlanCurrency } from "../../../../services/planService";
import {
  createPromotionCoupon,
  CreateSaasPromotionCouponPayload,
  CouponDurationType,
  deactivatePromotionCoupon,
  getPromotionCoupon,
  getPromotionCoupons,
  SaasPromotionCoupon,
  updatePromotionCoupon,
} from "../../../../services/superAdmin/saasPromotionCouponService";
import { PaginationMeta } from "../../../../types/api";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;

interface CouponFormValues {
  code: string;
  name: string;
  discount_percent: string;
  duration_type: CouponDurationType;
  duration_months: string;
  max_redemptions: string;
  valid_from: string;
  valid_until: string;
  applicable_currencies: PlanCurrency[];
  allow_on_upgrade: boolean;
  is_active: boolean;
}

const emptyFormValues = (): CouponFormValues => ({
  code: "",
  name: "",
  discount_percent: "",
  duration_type: "first_month",
  duration_months: "",
  max_redemptions: "100",
  valid_from: "",
  valid_until: "",
  applicable_currencies: ["BRL"],
  allow_on_upgrade: false,
  is_active: true,
});

const toDatetimeLocal = (iso: string): string => {
  const date = new Date(iso);

  const pad = (value: number): string => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIsoUtc = (localValue: string): string => new Date(localValue).toISOString();

const mapCouponToForm = (coupon: SaasPromotionCoupon): CouponFormValues => ({
  code: coupon.code,
  name: coupon.name,
  discount_percent: String(coupon.discount_percent_bps / 100),
  duration_type: coupon.duration_type,
  duration_months: coupon.duration_months != null ? String(coupon.duration_months) : "",
  max_redemptions: String(coupon.max_redemptions),
  valid_from: toDatetimeLocal(coupon.valid_from),
  valid_until: toDatetimeLocal(coupon.valid_until),
  applicable_currencies: coupon.applicable_currencies,
  allow_on_upgrade: coupon.allow_on_upgrade,
  is_active: coupon.is_active,
});

const buildPayload = (values: CouponFormValues): CreateSaasPromotionCouponPayload => ({
  code: values.code.trim().toUpperCase(),
  name: values.name.trim(),
  discount_percent_bps: Math.round(Number(values.discount_percent) * 100),
  duration_type: values.duration_type,
  duration_months:
    values.duration_type === "limited_months" ? Number(values.duration_months) : null,
  max_redemptions: Number(values.max_redemptions),
  valid_from: toIsoUtc(values.valid_from),
  valid_until: toIsoUtc(values.valid_until),
  applicable_currencies: values.applicable_currencies,
  allow_on_upgrade: values.allow_on_upgrade,
  is_active: values.is_active,
});

const SuperAdminPromotionCoupons = () => {
  const { t, i18n } = useTranslation();

  const [coupons, setCoupons] = useState<SaasPromotionCoupon[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<SaasPromotionCoupon | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const isCodeLocked = Boolean(editingCoupon && editingCoupon.redemption_count > 0);

  useEffect(() => {
    document.title = t("admin.promotionCoupons.pageTitle");
  }, [t, i18n.language]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getPromotionCoupons({
        page,
        per_page: PER_PAGE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(statusFilter === "active" ? { is_active: true } : {}),
        ...(statusFilter === "inactive" ? { is_active: false } : {}),
      });

      setCoupons(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("admin.promotionCoupons.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter, t]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        code: Yup.string()
          .trim()
          .matches(/^[A-Za-z0-9_-]+$/, t("admin.promotionCoupons.validation.codeFormat"))
          .required(t("admin.promotionCoupons.validation.codeRequired")),
        name: Yup.string().trim().required(t("admin.promotionCoupons.validation.nameRequired")),
        discount_percent: Yup.number()
          .typeError(t("admin.promotionCoupons.validation.discountType"))
          .min(0.01, t("admin.promotionCoupons.validation.discountMin"))
          .max(100, t("admin.promotionCoupons.validation.discountMax"))
          .required(t("admin.promotionCoupons.validation.discountRequired")),
        duration_type: Yup.mixed<CouponDurationType>()
          .oneOf(["first_month", "limited_months", "lifetime"])
          .required(),
        duration_months: Yup.string().when("duration_type", {
          is: "limited_months",
          then: (schema) =>
            schema
              .required(t("admin.promotionCoupons.validation.durationMonthsRequired"))
              .test("months-valid", t("admin.promotionCoupons.validation.durationMonthsMin"), (value) =>
                Number(value) >= 1
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
        max_redemptions: Yup.number()
          .typeError(t("admin.promotionCoupons.validation.maxRedemptionsType"))
          .integer()
          .min(1, t("admin.promotionCoupons.validation.maxRedemptionsMin"))
          .required(t("admin.promotionCoupons.validation.maxRedemptionsRequired")),
        valid_from: Yup.string().required(t("admin.promotionCoupons.validation.validFromRequired")),
        valid_until: Yup.string().required(t("admin.promotionCoupons.validation.validUntilRequired")),
      }),
    [t]
  );

  const formik = useFormik<CouponFormValues>({
    enableReinitialize: true,
    initialValues: editingCoupon ? mapCouponToForm(editingCoupon) : emptyFormValues(),
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);

      try {
        const payload = buildPayload(values);

        if (editingCoupon) {
          const updatePayload = { ...payload };

          if (isCodeLocked) {
            delete (updatePayload as Partial<CreateSaasPromotionCouponPayload>).code;
          }

          await updatePromotionCoupon(editingCoupon.id, updatePayload);
          toast.success(t("admin.promotionCoupons.toast.updated"));
        } else {
          await createPromotionCoupon(payload);
          toast.success(t("admin.promotionCoupons.toast.created"));
        }

        setOffcanvasOpen(false);
        setEditingCoupon(null);
        fetchCoupons();
      } catch (error: any) {
        toast.error(error?.data || t("admin.promotionCoupons.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const openCreate = (): void => {
    setEditingCoupon(null);
    formik.resetForm({ values: emptyFormValues() });
    setOffcanvasOpen(true);
  };

  const openEdit = useCallback(async (coupon: SaasPromotionCoupon): Promise<void> => {
    setFormLoading(true);
    setOffcanvasOpen(true);

    try {
      const response = await getPromotionCoupon(coupon.id);
      setEditingCoupon(response.data);
      formik.resetForm({ values: mapCouponToForm(response.data) });
    } catch (error: any) {
      toast.error(error?.data || t("admin.promotionCoupons.toast.loadError"));
      setOffcanvasOpen(false);
    } finally {
      setFormLoading(false);
    }
  }, [formik, t]);

  const handleDeactivate = useCallback(async (coupon: SaasPromotionCoupon): Promise<void> => {
    try {
      await deactivatePromotionCoupon(coupon.id);
      toast.success(t("admin.promotionCoupons.toast.deactivated"));
      fetchCoupons();
    } catch (error: any) {
      toast.error(error?.data || t("admin.promotionCoupons.toast.deactivateError"));
    }
  }, [fetchCoupons, t]);

  const columns = useMemo<ColumnDef<SaasPromotionCoupon>[]>(
    () => [
      {
        header: t("admin.promotionCoupons.table.code"),
        accessorKey: "code",
        cell: ({ row }) => <span className="fw-medium">{row.original.code}</span>,
      },
      {
        header: t("admin.promotionCoupons.table.name"),
        accessorKey: "name",
      },
      {
        header: t("admin.promotionCoupons.table.discount"),
        cell: ({ row }) => `${row.original.discount_percent_bps / 100}%`,
      },
      {
        header: t("admin.promotionCoupons.table.redemptions"),
        cell: ({ row }) =>
          `${row.original.redemption_count} / ${row.original.max_redemptions}`,
      },
      {
        header: t("admin.promotionCoupons.table.validUntil"),
        cell: ({ row }) =>
          new Intl.DateTimeFormat(i18n.language === "sp" ? "es" : i18n.language, {
            dateStyle: "medium",
          }).format(new Date(row.original.valid_until)),
      },
      {
        header: t("admin.promotionCoupons.table.status"),
        cell: ({ row }) => (
          <Badge color={row.original.is_active ? "success" : "secondary"}>
            {row.original.is_active
              ? t("admin.promotionCoupons.status.active")
              : t("admin.promotionCoupons.status.inactive")}
          </Badge>
        ),
      },
      {
        header: t("admin.promotionCoupons.table.actions"),
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button color="soft-primary" size="sm" outline onClick={() => openEdit(row.original)}>
              {t("admin.promotionCoupons.actions.edit")}
            </Button>
            {row.original.is_active ? (
              <Button
                color="soft-danger"
                size="sm"
                outline
                onClick={() => handleDeactivate(row.original)}
              >
                {t("admin.promotionCoupons.actions.deactivate")}
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [t, i18n.language, handleDeactivate, openEdit]
  );

  return (
    <div className="page-content">
      <ToastContainer closeButton pauseOnHover />
      <Container fluid>
        <BreadCrumb
          title={t("admin.promotionCoupons.breadcrumb.title")}
          pageTitle={t("admin.promotionCoupons.breadcrumb.pageTitle")}
        />

        <Card>
          <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h5 className="card-title mb-0">{t("admin.promotionCoupons.title")}</h5>
            <Button color="primary" size="sm" onClick={openCreate}>
              {t("admin.promotionCoupons.actions.create")}
            </Button>
          </CardHeader>
          <CardBody>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t("admin.promotionCoupons.filters.search")}
                />
              </Col>
              <Col md={3}>
                <Input
                  type="select"
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value as "" | "active" | "inactive")
                  }
                >
                  <option value="">{t("admin.promotionCoupons.filters.allStatuses")}</option>
                  <option value="active">{t("admin.promotionCoupons.status.active")}</option>
                  <option value="inactive">{t("admin.promotionCoupons.status.inactive")}</option>
                </Input>
              </Col>
            </Row>

            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={coupons}
                isGlobalFilter={false}
                customPageSize={PER_PAGE}
                tableClass="table align-middle"
                theadClass="table-light"
                divClass="table-responsive"
              />
            )}

            {meta && meta.last_page > 1 ? (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="text-muted small">
                  {t("admin.promotionCoupons.pagination.summary", {
                    total: meta.total,
                    page: meta.current_page,
                    lastPage: meta.last_page,
                  })}
                </span>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    color="light"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    {t("admin.promotionCoupons.pagination.previous")}
                  </Button>
                  <Button
                    size="sm"
                    color="light"
                    disabled={page >= meta.last_page}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    {t("admin.promotionCoupons.pagination.next")}
                  </Button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </Container>

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={() => !submitting && setOffcanvasOpen(false)}
        direction="end"
        style={{ width: "32rem" }}
      >
        <OffcanvasHeader toggle={() => !submitting && setOffcanvasOpen(false)}>
          {editingCoupon
            ? t("admin.promotionCoupons.form.editTitle")
            : t("admin.promotionCoupons.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Form onSubmit={formik.handleSubmit}>
              <div className="mb-3">
                <Label>{t("admin.promotionCoupons.form.code")}</Label>
                <Input
                  name="code"
                  value={formik.values.code}
                  onChange={formik.handleChange}
                  disabled={isCodeLocked || submitting}
                  invalid={Boolean(formik.touched.code && formik.errors.code)}
                />
                {isCodeLocked ? (
                  <div className="form-text">{t("admin.promotionCoupons.form.codeLocked")}</div>
                ) : null}
                <FormFeedback>{formik.errors.code}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label>{t("admin.promotionCoupons.form.name")}</Label>
                <Input
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  disabled={submitting}
                  invalid={Boolean(formik.touched.name && formik.errors.name)}
                />
                <FormFeedback>{formik.errors.name}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label>{t("admin.promotionCoupons.form.discount")}</Label>
                <Input
                  name="discount_percent"
                  type="number"
                  min={0.01}
                  max={100}
                  step={0.01}
                  value={formik.values.discount_percent}
                  onChange={formik.handleChange}
                  disabled={submitting}
                  invalid={Boolean(formik.touched.discount_percent && formik.errors.discount_percent)}
                />
                <FormFeedback>{formik.errors.discount_percent}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label>{t("admin.promotionCoupons.form.durationType")}</Label>
                <Input
                  type="select"
                  name="duration_type"
                  value={formik.values.duration_type}
                  onChange={formik.handleChange}
                  disabled={submitting}
                >
                  <option value="first_month">{t("admin.promotionCoupons.duration.firstMonth")}</option>
                  <option value="limited_months">{t("admin.promotionCoupons.duration.limitedMonths")}</option>
                  <option value="lifetime">{t("admin.promotionCoupons.duration.lifetime")}</option>
                </Input>
              </div>

              {formik.values.duration_type === "limited_months" ? (
                <div className="mb-3">
                  <Label>{t("admin.promotionCoupons.form.durationMonths")}</Label>
                  <Input
                    name="duration_months"
                    type="number"
                    min={1}
                    value={formik.values.duration_months}
                    onChange={formik.handleChange}
                    disabled={submitting}
                    invalid={Boolean(formik.touched.duration_months && formik.errors.duration_months)}
                  />
                  <FormFeedback>{formik.errors.duration_months}</FormFeedback>
                </div>
              ) : null}

              <div className="mb-3">
                <Label>{t("admin.promotionCoupons.form.maxRedemptions")}</Label>
                <Input
                  name="max_redemptions"
                  type="number"
                  min={1}
                  value={formik.values.max_redemptions}
                  onChange={formik.handleChange}
                  disabled={submitting}
                  invalid={Boolean(formik.touched.max_redemptions && formik.errors.max_redemptions)}
                />
                <FormFeedback>{formik.errors.max_redemptions}</FormFeedback>
              </div>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Label>{t("admin.promotionCoupons.form.validFrom")}</Label>
                  <Input
                    name="valid_from"
                    type="datetime-local"
                    value={formik.values.valid_from}
                    onChange={formik.handleChange}
                    disabled={submitting}
                  />
                </Col>
                <Col md={6}>
                  <Label>{t("admin.promotionCoupons.form.validUntil")}</Label>
                  <Input
                    name="valid_until"
                    type="datetime-local"
                    value={formik.values.valid_until}
                    onChange={formik.handleChange}
                    disabled={submitting}
                  />
                </Col>
              </Row>

              <div className="mb-3">
                <Label>{t("admin.promotionCoupons.form.currencies")}</Label>
                <div className="d-flex gap-3">
                  {(["BRL", "USD"] as PlanCurrency[]).map((currency) => (
                    <div className="form-check" key={currency}>
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id={`currency_${currency}`}
                        checked={formik.values.applicable_currencies.includes(currency)}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...formik.values.applicable_currencies, currency]
                            : formik.values.applicable_currencies.filter(
                                (item) => item !== currency
                              );
                          formik.setFieldValue("applicable_currencies", next);
                        }}
                        disabled={submitting}
                      />
                      <Label check htmlFor={`currency_${currency}`} className="form-check-label">
                        {currency}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-check form-switch mb-3">
                <Input
                  type="checkbox"
                  role="switch"
                  id="allow_on_upgrade"
                  name="allow_on_upgrade"
                  checked={formik.values.allow_on_upgrade}
                  onChange={formik.handleChange}
                  disabled={submitting}
                />
                <Label check htmlFor="allow_on_upgrade" className="ms-2">
                  {t("admin.promotionCoupons.form.allowOnUpgrade")}
                </Label>
              </div>

              <div className="form-check form-switch mb-4">
                <Input
                  type="checkbox"
                  role="switch"
                  id="is_active"
                  name="is_active"
                  checked={formik.values.is_active}
                  onChange={formik.handleChange}
                  disabled={submitting}
                />
                <Label check htmlFor="is_active" className="ms-2">
                  {t("admin.promotionCoupons.form.isActive")}
                </Label>
              </div>

              <Button color="primary" type="submit" disabled={submitting} className="w-100">
                {submitting ? <Spinner size="sm" /> : t("admin.promotionCoupons.form.save")}
              </Button>
            </Form>
          )}
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
};

export default SuperAdminPromotionCoupons;
